import React, { Component } from 'react';
import CacheKeys from '../../../cache/CacheKeys';
import CacheManager from '../../../cache/CacheManager';
import { withRouter } from 'react-router-dom';
import MISummaryUI from './MISummaryUI';
import RestApiUtils from '../../../apiclient/moebli/RestApiUtils';

/**
 * MISummarySF
 *
 * Fetches monitoring intelligence data and passes processed data to MISummaryUI.
 *
 * FIX 1: activeAccounts from the API already reflects all disbursed loans across
 * the system (computed in MonitoringIntelligenceListGetResponseDTO.copy).
 * The DTO now counts ALL disbursed-status loans from loanEntityList regardless
 * of whether they appear in the MI report data.  We simply forward that count.
 *
 * FIX 5: activeAccountsList (accountId + accountName) is now also forwarded
 * from the API so the UI can compute exactly which active accounts have NO
 * financial/non-financial issue this month ("Good Standing") and hyperlink
 * them, the same way non-compliant/legal accounts are hyperlinked.
 */
class MISummarySF extends Component {
  constructor(props) {
    super(props);
    this._isMounted = false;
    this.state = {
      errorList: null,
      rawList: null,
      processedData: [],
      nonFinancialComplianceData: {},
      yearMonth: null,
      year: '',
      month: '',
      previousYear: '',
      previousMonth: '',
      nextYear: '',
      nextMonth: '',
      loading: false,
      activeAccounts: 0,
      activeAccountsList: [],
      miReportExists: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.getMonitoringIntelligenceReport(this.props.context, '', '');
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // ─── API fetch ────────────────────────────────────────────────────────────

  getMonitoringIntelligenceReport = (context, year, month) => {
    this.setState({ loading: true });
    year = year === undefined ? '' : year;
    month = month === undefined ? '' : month;

    const cacheManager = new CacheManager({
      resource: '/api/su/monitoringIntelligence',
      cacheKey: CacheKeys.ChequeBouncesList(year, month),
      callback: this.onDataCallback,
      ttlInSecs: 600,
    });
    cacheManager.getObject2(context, {}, { year: year, month: month });
  };

  onDataCallback = (response) => {
    if (!this._isMounted) return;
    const rawList = response.monitoringIntelligenceList || [];
    const { processedData, nonFinancialComplianceData } = this.processData(response);

    if (response.yearMonth) {
      this.checkReportStatus(response.yearMonth.year, response.yearMonth.month);
    }

    this.setState({
      rawList: rawList,
      processedData: processedData,
      nonFinancialComplianceData: nonFinancialComplianceData,
      errorList: response.errorList || [],
      loading: false,
      yearMonth: response.yearMonth,
      year: response.yearMonth ? response.yearMonth.year : '',
      month: response.yearMonth ? response.yearMonth.month : '',
      previousYear: response.yearMonth ? response.yearMonth.prevYear : '',
      previousMonth: response.yearMonth ? response.yearMonth.prevMonth : '',
      nextYear: response.yearMonth ? response.yearMonth.nextYear : '',
      nextMonth: response.yearMonth ? response.yearMonth.nextMonth : '',
      // FIX 1: use activeAccounts directly from the API — it already counts
      // ALL disbursed loans across the system, not just those with issues.
      activeAccounts: typeof response.activeAccounts === 'number' ? response.activeAccounts : 0,
      // FIX 5: full active account list (accountId + accountName) from the API.
      activeAccountsList: Array.isArray(response.activeAccountsList)
        ? response.activeAccountsList
        : [],
    });
  };

  checkReportStatus = (year, month) => {
    if (!this._isMounted) return;
    RestApiUtils.get(
      this.props.context,
      `/api/su/monitoringIntelligence/miReportStatus?year=${year}&month=${month}`,
      (data) => {
        if (this._isMounted) {
          this.setState({ miReportExists: data.success === 'EXISTS' });
        }
      },
      null
    );
  };

  onPreviousClick = () => {
    this.getMonitoringIntelligenceReport(
      this.props.context,
      this.state.previousYear,
      this.state.previousMonth
    );
  };

  onNextClick = () => {
    this.getMonitoringIntelligenceReport(
      this.props.context,
      this.state.nextYear,
      this.state.nextMonth
    );
  };

  onRefreshClick = () => {
    CacheManager.removeObject(
      CacheKeys.ChequeBouncesList(this.state.year, this.state.month)
    );
    this.setState(
      { rawList: null, errorList: null, processedData: [] },
      () => {
        this.getMonitoringIntelligenceReport(
          this.props.context,
          this.state.year,
          this.state.month
        );
      }
    );
  };

  // ─── Data processing ──────────────────────────────────────────────────────

  cleanNumber = (num) => {
    if (typeof num === 'string') return parseFloat(num.replace(/,/g, '')) || 0;
    return Number(num) || 0;
  };

  processData = (data) => {
    if (!data || !data.monitoringIntelligenceList) {
      return { processedData: [], nonFinancialComplianceData: {} };
    }

    const processedData = [];
    const nonFinancialComplianceData = {};
    let serialCounter = 1;
    let currentAccountId = null;
    const processedDsraAccounts = new Set();

    data.monitoringIntelligenceList.forEach((account) => {
      if (currentAccountId !== account.accountId) {
        currentAccountId = account.accountId;
        if (processedData.length > 0) {
          serialCounter++;
        }
      }

      const loans = account.loans ? account.loans : [];
      loans.forEach((loan) => {
        const nfList = loan.nonFinancialCompliances ? loan.nonFinancialCompliances : [];
        if (nfList.length > 0) {
          if (!nonFinancialComplianceData[account.accountName]) {
            nonFinancialComplianceData[account.accountName] = [];
          }
          nfList.forEach((compliance) => {
            nonFinancialComplianceData[account.accountName].push({
              ...compliance,
              lrn: loan.lrn,
              loanId: loan.loanId,
              accountId: account.accountId,
            });
          });
        }
      });

      loans.forEach((loan) => {
        const pushRow = (item, index, listKey) => {
          processedData.push({
            serialNumber: serialCounter,
            accountName: account.accountName,
            isFirstInAccount:
              index === 0 &&
              !processedData.some((e) => e.accountName === account.accountName),
            isAccountLevel: false,
            item: {
              ...item,
              loanId: loan.loanId,
              lrn: loan.lrn || 'N/A',
              accountId: account.accountId,
            },
          });
        };

        const emiData = loan.emiData ? loan.emiData : [];
        emiData.forEach((item, i) => pushRow(item, i, 'emiData'));

        const legalData = loan.legalChargeData ? loan.legalChargeData : [];
        legalData.forEach((item, i) => pushRow(item, i, 'legalChargeData'));

        const penalData = loan.penalChargeData ? loan.penalChargeData : [];
        penalData.forEach((item, i) => pushRow(item, i, 'penalChargeData'));
      });

      const nachFailData = account.nachFailData ? account.nachFailData : [];
      nachFailData.forEach((item, i) => {
        processedData.push({
          serialNumber: serialCounter,
          accountName: account.accountName,
          isFirstInAccount:
            i === 0 &&
            !processedData.some((e) => e.accountName === account.accountName),
          isAccountLevel: true,
          item: { ...item, accountId: account.accountId },
        });
      });

      const dsraShortFall = account.dsraShortFall ? account.dsraShortFall : [];
      dsraShortFall.forEach((item, i) => {
        if (processedDsraAccounts.has(item.accountId)) return;
        processedData.push({
          serialNumber: serialCounter,
          accountName: account.accountName,
          isFirstInAccount:
            i === 0 &&
            !processedData.some((e) => e.accountName === account.accountName),
          isAccountLevel: true,
          item: {
            ...item,
            accountId: account.accountId,
            amount: item.dsra,
            amountReceived: item.dsraCur,
            amountPending: item.dsraDip,
          },
        });
        processedDsraAccounts.add(item.accountId);
      });
    });

    return { processedData, nonFinancialComplianceData };
  };

  // ─── Build accountGroups + accountSummaries + dsraByAccountMap + accountMonitoringMap
  buildDerivedData = () => {
    const { processedData, nonFinancialComplianceData, rawList } = this.state;

    const accountGroups = {};
    const accountSummaries = {};
    // FIX 4: DSRA by account
    const dsraByAccountMap = {};
    // FIX 3: monitoring comment / action by account (from dsraShortFall)
    const accountMonitoringMap = {};

    // Build loanId map from raw API data
    const loanIdMap = {};
    (rawList || []).forEach((account) => {
      if (!loanIdMap[account.accountName]) {
        const loans = account.loans || [];
        for (const loan of loans) {
          if (loan.loanId) {
            loanIdMap[account.accountName] = loan.loanId;
            break;
          }
        }
      }

      // FIX 4: collect DSRA shortfall data per account
      if (account.dsraShortFall && account.dsraShortFall.length > 0) {
        dsraByAccountMap[account.accountName] = account.dsraShortFall;
      }

      // FIX 3: collect monitoring action/comment from dsraShortFall
      if (account.dsraShortFall && account.dsraShortFall.length > 0) {
        const dsra = account.dsraShortFall[0];
        if (dsra.monitoringAction || dsra.monitoringComment) {
          accountMonitoringMap[account.accountName] = {
            monitoringAction: dsra.monitoringAction || '',
            monitoringComment: dsra.monitoringComment || '',
          };
        }
      }
    });

    const dsraShortfallMap = {};
    processedData.forEach((row) => {
      const { accountName, item } = row;
      if (item.reportType === 'dsraShortfall') {
        dsraShortfallMap[accountName] = {
          dsra: this.cleanNumber(item.dsra ? item.dsra.toString() : '0'),
          dsraCur: this.cleanNumber(item.dsraCur ? item.dsraCur.toString() : '0'),
          dsraDip: this.cleanNumber(item.dsraDip ? item.dsraDip.toString() : '0'),
        };
      }
    });

    processedData.forEach((row) => {
      const { accountName, item } = row;

      if (item.reportType === 'emi' && (!item.lrn || item.lrn === 'N/A')) return;

      if (!accountGroups[accountName]) accountGroups[accountName] = [];
      accountGroups[accountName].push(row);

      if (!accountSummaries[accountName]) {
        accountSummaries[accountName] = {
          totalAmountDue: 0,
          totalAmountReceived: 0,
          totalAmountPending: 0,
          totalOverdueInterest: 0,
          loans: {},
          accountId: item.accountId,
          dsraIncluded: false,
        };
      }

      const isValid = ['emi', 'legalCharge', 'penalCharge'].includes(item.reportType);
      if (isValid) {
        accountSummaries[accountName].totalAmountDue += this.cleanNumber(item.amount);
        accountSummaries[accountName].totalAmountReceived += this.cleanNumber(item.amountReceived);
        accountSummaries[accountName].totalAmountPending += this.cleanNumber(item.amountPending);
        if (item.reportType === 'emi') {
          accountSummaries[accountName].totalOverdueInterest += this.cleanNumber(item.overdueInterest);
        }
        if (item.lrn && item.lrn !== 'N/A') {
          if (!accountSummaries[accountName].loans[item.lrn]) {
            accountSummaries[accountName].loans[item.lrn] = {
              loanId: item.loanId,
              totalDue: 0,
              totalReceived: 0,
              totalPending: 0,
              totalOverdueInterest: 0,
            };
          }
          accountSummaries[accountName].loans[item.lrn].totalDue += this.cleanNumber(item.amount);
          accountSummaries[accountName].loans[item.lrn].totalReceived += this.cleanNumber(item.amountReceived);
          accountSummaries[accountName].loans[item.lrn].totalPending += this.cleanNumber(item.amountPending);
          if (item.reportType === 'emi') {
            accountSummaries[accountName].loans[item.lrn].totalOverdueInterest += this.cleanNumber(item.overdueInterest);
          }
        }
      }
    });

    Object.entries(dsraShortfallMap).forEach(([accountName, dsra]) => {
      if (accountSummaries[accountName] && !accountSummaries[accountName].dsraIncluded) {
        accountSummaries[accountName].totalAmountDue += dsra.dsra;
        accountSummaries[accountName].totalAmountReceived += dsra.dsraCur;
        accountSummaries[accountName].totalAmountPending += dsra.dsraDip;
        accountSummaries[accountName].dsraIncluded = true;
      }
    });

    // Seed accounts with only dsraShortfall data (e.g. Clime, Whizz, Verdeen)
    (rawList || []).forEach((account) => {
      const name = account.accountName;
      if (!accountSummaries[name]) {
        const dsraList = account.dsraShortFall || [];
        let totalDue = 0, totalReceived = 0, totalPending = 0;
        dsraList.forEach((d) => {
          totalDue += this.cleanNumber(d.dsra ? d.dsra.toString() : '0');
          totalReceived += this.cleanNumber(d.dsraCur ? d.dsraCur.toString() : '0');
          totalPending += this.cleanNumber(d.dsraDip ? d.dsraDip.toString() : '0');
        });
        accountSummaries[name] = {
          totalAmountDue: totalDue,
          totalAmountReceived: totalReceived,
          totalAmountPending: totalPending,
          totalOverdueInterest: 0,
          loans: {},
          accountId: account.accountId,
          dsraIncluded: dsraList.length > 0,
        };
      }
      if (!accountGroups[name]) {
        accountGroups[name] = [];
      }
    });

    Object.entries(nonFinancialComplianceData).forEach(([accountName, compliances]) => {
      if (!accountGroups[accountName]) accountGroups[accountName] = [];
      if (!accountSummaries[accountName]) {
        const first = compliances[0];
        accountSummaries[accountName] = {
          totalAmountDue: 0,
          totalAmountReceived: 0,
          totalAmountPending: 0,
          totalOverdueInterest: 0,
          loans: {},
          accountId: first ? first.accountId : null,
          dsraIncluded: false,
        };
      }
    });

    return { accountGroups, accountSummaries, loanIdMap, dsraByAccountMap, accountMonitoringMap };
  };

  render() {
    const { context } = this.props;
    const {
      errorList,
      rawList,
      nonFinancialComplianceData,
      yearMonth,
      loading,
      activeAccounts,
      activeAccountsList,
      miReportExists,
    } = this.state;

    const { accountGroups, accountSummaries, loanIdMap, dsraByAccountMap, accountMonitoringMap } =
      this.buildDerivedData();

    return (
      <MISummaryUI
        context={context}
        errorList={errorList}
        rawList={rawList}
        accountGroups={accountGroups}
        accountSummaries={accountSummaries}
        nonFinancialComplianceData={nonFinancialComplianceData}
        yearMonth={yearMonth}
        loading={loading}
        activeAccounts={activeAccounts}
        activeAccountsList={activeAccountsList}
        loanIdMap={loanIdMap}
        miReportExists={miReportExists}
        dsraByAccountMap={dsraByAccountMap}
        accountMonitoringMap={accountMonitoringMap}
        onPreviousClick={this.onPreviousClick}
        onNextClick={this.onNextClick}
        onRefreshClick={this.onRefreshClick}
      />
    );
  }
}

export default withRouter(MISummarySF);